import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Edit, Loader2, Bot } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  listAIProviders,
  toggleAIProviderStatus,
} from "@/services/aiProvidersService";
import type { AIProvider } from "@/types/aiProviders";
import { AIProviderForm } from "./AIProviderForm";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/auth/PermissionGuard";

interface AIProviderManagementProps {
  onBack: () => void;
}

type ViewMode = "list" | "form";

export const AIProviderManagement = ({ onBack }: AIProviderManagementProps) => {
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [editingProvider, setEditingProvider] = useState<AIProvider | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { canCreate, canUpdate } = usePermissions();

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    setLoading(true);
    try {
      const response = await listAIProviders({ limit: 100 });

      if (response.success && response.data) {
        setProviders(response.data.providers);
      } else {
        toast({
          title: "Erro ao carregar provedores",
          description: response.error?.message || "Erro desconhecido",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar provedores",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProvider = async () => {
    await loadProviders();
    setViewMode("list");
    setEditingProvider(null);
  };

  const handleNewProvider = () => {
    setEditingProvider(null);
    setViewMode("form");
  };

  const handleEdit = (provider: AIProvider) => {
    setEditingProvider(provider);
    setViewMode("form");
  };

  const handleToggleStatus = async (providerId: number) => {
    try {
      const response = await toggleAIProviderStatus(providerId);

      if (response.success) {
        toast({
          title: "Status alterado",
          description: "O status do provedor foi alterado com sucesso",
        });
        await loadProviders();
      } else {
        throw new Error(response.error?.message || "Erro ao alterar status");
      }
    } catch (error) {
      toast({
        title: "Erro",
        description:
          error instanceof Error ? error.message : "Erro ao alterar status",
        variant: "destructive",
      });
    }
  };

  if (viewMode === "form") {
    return (
      <AIProviderForm
        onBack={() => setViewMode("list")}
        onSave={handleSaveProvider}
        editingProvider={editingProvider}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando provedores...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard
      permission="res_providers.read"
      fallback={
        <div className="min-h-screen flex items-center justify-center p-8">
          <Card className="max-w-md w-full">
            <CardHeader>
              <CardTitle>Acesso negado</CardTitle>
              <CardDescription>Você não tem permissão para visualizar provedores de IA.</CardDescription>
            </CardHeader>
          </Card>
        </div>
      }
    >
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        icon={Bot}
        title="Provedores de IA"
        description="Configure os provedores de modelos de IA disponíveis"
        onBack={onBack}
        actions={
          canCreate("providers") && (
            <Button
              className="bg-gradient-primary text-white hover:opacity-90"
              onClick={handleNewProvider}
            >
              <Plus className="h-4 w-4 mr-2" />
              Novo Provedor
            </Button>
          )
        }
      />

      <main className="container mx-auto px-6 py-6">
        <Card>
          <CardHeader>
            <CardTitle>Provedores Cadastrados</CardTitle>
            <CardDescription>
              Lista de todos os provedores de IA configurados
            </CardDescription>
          </CardHeader>
          <CardContent>
            {providers.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Bot className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-4">
                    Nenhum provedor cadastrado
                  </p>
                  <Button onClick={handleNewProvider} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Criar Primeiro Provedor
                  </Button>
                </div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Criado em</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {providers.map((provider) => (
                    <TableRow key={provider.id}>
                      <TableCell>
                        <div className="font-medium">{provider.name}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {provider.provider_id || "-"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-md truncate">
                          {provider.description || "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        {provider.is_active ? (
                          <Badge
                            variant="default"
                            className="bg-green-500/10 text-green-700"
                          >
                            Ativo
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Inativo</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(provider.created_at).toLocaleDateString(
                          "pt-BR"
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          {canUpdate("providers") && (
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={() => handleEdit(provider)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {canUpdate("providers") && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleStatus(provider.id)}
                              className={
                                provider.is_active
                                  ? "hover:bg-destructive/10 hover:text-destructive"
                                  : "hover:bg-green-50 hover:text-green-700"
                              }
                            >
                              {provider.is_active ? "Desativar" : "Ativar"}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
    </PermissionGuard>
  );
};

